import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import api from '../api';

export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
const ERLAUBTE_TYPEN = ['image/jpeg', 'image/png', 'image/webp'];

export function formatGroesse(bytes) {
  if (bytes === null || bytes === undefined) return '–';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

// Prüft Typ und Größe schon im Browser: Ein zu großes Handyfoto wird dann gar
// nicht erst übertragen, und die Meldung kommt sofort. Die verbindliche Prüfung
// macht trotzdem der Server.
export async function ladeBildHoch(file) {
  if (!ERLAUBTE_TYPEN.includes(file.type)) {
    throw new Error(`„${file.name}": Nur JPG, PNG und WebP sind erlaubt.`);
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`„${file.name}" ist ${formatGroesse(file.size)} groß – erlaubt sind maximal 2 MB.`);
  }
  const formData = new FormData();
  formData.append('bild', file);
  try {
    const r = await api.post('/api/admin/bilder', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || `Upload von „${file.name}" fehlgeschlagen.`);
  }
}

/**
 * Dialog zum Wählen eines Bildes: neu hochladen ODER aus der Bildbibliothek.
 *
 * onAuswahl(bildId | null) muss ein Promise liefern. Fehler daraus zeigt der
 * Dialog selbst an – der Aufrufer schließt ihn nur im Erfolgsfall.
 */
export default function BildAuswahl({ titel = 'Bild auswählen', aktuellerPfad, onAuswahl, onClose }) {
  const [bilder, setBilder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/api/admin/bilder')
      .then(r => setBilder(r.data))
      .catch(() => setFehler('Die Bildbibliothek konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  const waehle = async (bildId) => {
    setBusy(true);
    setFehler('');
    try {
      await onAuswahl(bildId);
    } catch (err) {
      setFehler(err.response?.data?.error || err.message || 'Das Bild konnte nicht übernommen werden.');
    } finally {
      setBusy(false);
    }
  };

  const hochladen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setFehler('');
    try {
      const bild = await ladeBildHoch(file);
      // Schon jetzt in die Liste, damit es auch dann sichtbar ist, wenn das
      // anschließende Zuweisen scheitert.
      setBilder(b => [bild, ...b]);
      await onAuswahl(bild.id);
    } catch (err) {
      setFehler(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={titel} onClose={onClose} size="lg">
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <label className="btn btn-primary btn-sm" style={{ cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Bitte warten…' : '⬆ Neues Bild hochladen'}
          <input type="file" accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }} disabled={busy} onChange={hochladen} />
        </label>
        {aktuellerPfad && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => waehle(null)}>
            Bild entfernen
          </button>
        )}
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          JPG, PNG oder WebP bis 2 MB – wird automatisch auf höchstens 500 KB verkleinert.
        </span>
      </div>

      {fehler && <div className="alert alert-error">{fehler}</div>}

      <div style={{ fontWeight: 600, fontSize: '0.85rem', margin: '0.5rem 0 0.625rem' }}>
        Oder ein vorhandenes Bild aus der Bibliothek wählen
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '1.5rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      ) : bilder.length === 0 ? (
        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem', background: '#f8fafc', borderRadius: 8 }}>
          Die Bibliothek ist noch leer – lade oben das erste Bild hoch.
        </div>
      ) : (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '0.625rem', maxHeight: '55vh', overflowY: 'auto', padding: 2,
        }}>
          {bilder.map(b => {
            const aktuell = b.pfad === aktuellerPfad;
            const gesperrt = busy || aktuell || b.datei_fehlt;
            return (
              <button key={b.id} type="button" disabled={gesperrt} onClick={() => waehle(b.id)}
                title={b.original_name || b.pfad}
                style={{
                  padding: 0, textAlign: 'left', background: '#fff', overflow: 'hidden', borderRadius: 8,
                  border: aktuell ? '3px solid var(--primary)' : '1px solid var(--border)',
                  cursor: gesperrt ? 'default' : 'pointer',
                }}>
                <div style={{ height: 96, background: '#f0f4f8' }}>
                  {b.datei_fehlt ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', color: '#c62828' }}>
                      Datei fehlt
                    </div>
                  ) : (
                    <img src={`/uploads/${b.pfad}`} alt="" loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  )}
                </div>
                <div style={{ padding: '0.3rem 0.45rem', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  {aktuell
                    ? <strong style={{ color: 'var(--primary)' }}>Aktuelles Bild</strong>
                    : (b.verwendet_in.length ? `${b.verwendet_in.length}× verwendet` : 'unbenutzt')}
                  {' · '}{formatGroesse(b.groesse_bytes)}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
